import { z } from 'zod'

import { HTTP_METHOD } from '@nxt-insurance/api-service'
import { NO_INVALID_CHARACTER_USERNAME_REGEX, SupportedMarketKey } from '@nxt-insurance/constants'

import {
  MARKETING_CONSENT_KEY_TO_MARKET_MAP,
  getPhoneNumberSchema,
} from '@flows/configs/common/steps/email_and_name'
import { getLocationSchema } from '@flows/configs/common/steps/location'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import * as validators from '@flows/configs/shared/validators'
import { createRefineDateSchema } from '@flows/configs/shared/validators'
import type { StepConfigParams } from '@flows/configs/types'
import { t } from '@flows/core/i18n'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { MAXIMUM_CUSTOMER_AGE, MINIMUM_CUSTOMER_AGE } from '@flows/insurance/constants'
import type { StepName } from '@flows/insurance/types/stepNameTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'
import { getTransientPath } from '@flows/insurance/utils'

const nameSchema = z
  .string()
  .min(2)
  .max(50)
  .regex(
    NO_INVALID_CHARACTER_USERNAME_REGEX,
    t('sharedFlows_common:generic.errors.name_invalid_characters')
  )

type PersonalDetailsTranslations = {
  title: string
  birthdateLabel: string
  marketingOptIn: string
  minAgeError: string
  maxAgeError: string
}

type PersonalDetailsParams = {
  nextStep: StepName
  translations: PersonalDetailsTranslations
  omitFields?: readonly ('address' | 'birthdate')[]
  lockBirthdate?: boolean
  policyPreviewConsent?: { titleKey: string; textKey: string }
  maxAge?: number
  /** Report email and phone as soon as they are filled in, so an abandoned form can be retargeted. */
  trackContactBlur?: boolean
  /** `new`: labels above fields, placeholders, names side by side, consent below the phone. */
  layout?: 'default' | 'new'
}

const translatable = (translationKey: string) =>
  ({ type: 'translatableText', translationKey }) as const

const NEW_LAYOUT_SPACING = {
  question: { isQuestion: true, spaceBefore: 'largeMedium' },
  field: { spaceBefore: 'mini' },
  autocomplete: { spaceBefore: 'small' },
} as const

const DEFAULT_LAYOUT_SPACING = { question: {}, field: {}, autocomplete: {} } as const

const nameInput = (name: 'first_name' | 'last_name', labelKey: string, placeholderKey: string) =>
  ({
    type: 'input',
    name,
    styleType: 'outlined',
    disabledIfValueExists: true,
    labelAbove: true,
    label: translatable(labelKey),
    placeholder: translatable(placeholderKey),
  }) as const

export const personal_details = (
  params: StepConfigParams,
  {
    nextStep,
    translations,
    omitFields = [],
    lockBirthdate,
    policyPreviewConsent,
    maxAge = MAXIMUM_CUSTOMER_AGE.de,
    trackContactBlur,
    layout = 'default',
  }: PersonalDetailsParams
) => {
  const spacing = layout === 'new' ? NEW_LAYOUT_SPACING : DEFAULT_LAYOUT_SPACING
  const contactBlurHook = (name: 'onEmailBlur' | 'onPhoneBlur') =>
    trackContactBlur
      ? ({
          onBlur: {
            type: 'dispatchedHook',
            callbackConfig: {
              name,
              options: { productKey: params.productKey },
              inputs: [],
              outputs: [],
            },
          },
        } as const)
      : {}

  const phoneNumberSchema = getPhoneNumberSchema()
  const policyPreviewFields = policyPreviewConsent
    ? ([
        {
          type: 'checkbox',
          blockTitle: {
            type: 'translatableText',
            translationKey: policyPreviewConsent.titleKey,
          },
          fields: [
            {
              name: 'legalDocumentsEmailOptin',
              title: {
                type: 'translatableText',
                translationKey: policyPreviewConsent.textKey,
              },
            },
          ],
          bgColor: 'grey100',
        },
      ] as const)
    : ([] as const)
  const birthdateFields = omitFields.includes('birthdate')
    ? ([] as const)
    : ([
        {
          type: 'title',
          ...spacing.question,
          text: {
            type: 'translatableText',
            translationKey: translations.birthdateLabel,
          },
        },
        {
          type: 'date',
          name: 'born_on',
          styleType: 'outlined',
          disabledWhenSet: lockBirthdate,
          ...spacing.field,
        },
      ] as const)
  const addressFields = omitFields.includes('address')
    ? ([] as const)
    : ([
        {
          type: 'title',
          ...spacing.question,
          text: {
            type: 'translatableText',
            translationKey: 'sharedFlows_common:steps.email_and_name.address.input.label',
          },
        },
        {
          type: 'infoBox',
          showInfoIcon: true,
          bgColor: 'grey100',
          ...spacing.field,
          contentConfiguration: [
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey: 'sharedFlows_common:steps.personal_details.address.input.infobox',
              },
            },
          ],
        },
        {
          type: 'addressAutocomplete',
          name: 'address_autocomplete',
          ...spacing.autocomplete,
          label: {
            type: 'translatableText',
            translationKey: 'sharedFlows_common:steps.address_autocomplete.label',
          },
        },
      ] as const)

  const defaultContactFields = [
    {
      type: 'spacer',
      size: 'xsmall',
    },
    {
      type: 'title',
      text: {
        type: 'translatableText',
        translationKey: 'sharedFlows_common:steps.email_and_name.email.input.label',
      },
    },
    {
      type: 'input',
      name: 'email',
      styleType: 'outlined',
      disabledIfValueExists: true,
      ...contactBlurHook('onEmailBlur'),
      label: {
        type: 'translatableText',
        translationKey: 'sharedFlows_common:steps.email_and_name.email.input.label',
      },
    },
    {
      type: 'title',
      text: {
        type: 'translatableText',
        translationKey: 'sharedFlows_common:steps.email_and_name.firstName.input.label',
      },
    },
    {
      type: 'input',
      name: 'first_name',
      styleType: 'outlined',
      disabledIfValueExists: true,
      label: {
        type: 'translatableText',
        translationKey: 'sharedFlows_common:steps.email_and_name.firstName.input.label',
      },
    },
    {
      type: 'title',
      text: {
        type: 'translatableText',
        translationKey: 'sharedFlows_common:steps.email_and_name.lastName.input.label',
      },
    },
    {
      type: 'input',
      name: 'last_name',
      styleType: 'outlined',
      disabledIfValueExists: true,
      label: {
        type: 'translatableText',
        translationKey: 'sharedFlows_common:steps.email_and_name.lastName.input.label',
      },
    },
    ...birthdateFields,
    {
      type: 'title',
      text: {
        type: 'translatableText',
        translationKey: 'sharedFlows_common:steps.email_and_name.phone.input.label',
      },
    },
    {
      type: 'phoneNumber',
      name: 'phone_number',
      disabledIfValueExists: true,
      ...contactBlurHook('onPhoneBlur'),
      label: {
        type: 'translatableText',
        translationKey: 'sharedFlows_common:steps.email_and_name.phone.input.label',
      },
      defaultCountry: SupportedMarketKey.De,
      styleType: 'outlined',
    },
    ...addressFields,
    {
      type: 'spacer',
      size: 'medium',
    },
    {
      type: 'checkbox',
      blockTitle: {
        type: 'translatableText',
        translationKey: 'sharedFlows_common:steps.email_and_name.checkboxes.title',
      },
      fields: [
        {
          name: 'marketingOptIn',
          title: {
            type: 'translatableText',
            translationKey: translations.marketingOptIn,
          },
          isOptional: true,
        },
      ],
      bgColor: 'grey100',
    },
    ...policyPreviewFields,
  ] as const

  const newContactFields = [
    {
      type: 'columns',
      stackOnMobile: true,
      spaceBefore: 'small',
      spaceBeforeDesktop: 'large',
      content: [
        nameInput(
          'first_name',
          'sharedFlows_common:steps.email_and_name.firstName.input.label',
          'sharedFlows_common:steps.personal_details.placeholders.firstName'
        ),
        nameInput(
          'last_name',
          'sharedFlows_common:steps.email_and_name.lastName.input.label',
          'sharedFlows_common:steps.personal_details.placeholders.lastName'
        ),
      ],
    },
    {
      type: 'title',
      ...NEW_LAYOUT_SPACING.question,
      text: translatable('sharedFlows_common:steps.email_and_name.email.input.label'),
    },
    {
      type: 'input',
      name: 'email',
      inputType: 'email',
      styleType: 'outlined',
      disabledIfValueExists: true,
      ...NEW_LAYOUT_SPACING.field,
      ...contactBlurHook('onEmailBlur'),
      placeholder: translatable('sharedFlows_common:steps.personal_details.placeholders.email'),
    },
    {
      type: 'title',
      ...NEW_LAYOUT_SPACING.question,
      text: translatable('sharedFlows_common:steps.email_and_name.phone.input.label'),
    },
    {
      type: 'phoneNumber',
      name: 'phone_number',
      disabledIfValueExists: true,
      ...NEW_LAYOUT_SPACING.field,
      ...contactBlurHook('onPhoneBlur'),
      defaultCountry: SupportedMarketKey.De,
      styleType: 'outlined',
      placeholder: translatable(
        'sharedFlows_common:steps.personal_details.placeholders.phoneNumber'
      ),
    },
    {
      type: 'checkbox',
      spaceBefore: 'small',
      fields: [
        {
          name: 'phoneConsent',
          title: translatable('sharedFlows_common:steps.personal_details.marketingConsent'),
          isOptional: true,
        },
      ],
      bgColor: 'grey100',
    },
    ...policyPreviewFields,
    ...birthdateFields,
    ...addressFields,
  ] as const

  return {
    schema: getLocationSchema(SupportedMarketKey.De).merge(
      schemaUtils.generateZodObjectFromPaths({
        email: z.string().email(),
        phone_number: phoneNumberSchema,
        born_on: validators.createPreprocessedDateSchema(
          createRefineDateSchema({
            minAge: {
              errorMessage: t(translations.minAgeError),
              year: MINIMUM_CUSTOMER_AGE.de,
            },
            maxAge: {
              errorMessage: t(translations.maxAgeError),
              year: maxAge,
            },
          })
        ),
        first_name: nameSchema,
        last_name: nameSchema,
        marketingOptIn: z.boolean().optional(),
        phoneConsent: z.boolean().optional(),
        legalDocumentsEmailOptin: policyPreviewConsent ? z.literal(true) : z.boolean().optional(),
      })
    ),
    onPrepare: {
      type: 'simpleHook',
      callback: async () => {
        if (!params.market) {
          throw new Error('Assertion: Market is undefined on preparing personal details')
        }

        const response = await params.clientHelpers.apiService.call({
          serviceName: 'personalizationService',
          method: HTTP_METHOD.GET,
          url: `/api/v3/legal_texts/versioned?market=${params.market}`,
        })

        const marketingConsentText =
          response.data[MARKETING_CONSENT_KEY_TO_MARKET_MAP[params.market]]

        if (!marketingConsentText?.contentVersion.contentHtml) {
          throw new Error('Assertion: marketing consent text in undefined')
        }

        return {
          formValues: {
            [getTransientPath('marketing_consent_text')]:
              marketingConsentText.contentVersion.contentHtml,
            [getTransientPath('marketing_consent_version')]:
              marketingConsentText.contentVersion.key,
          },
        }
      },
    },
    stepContent: [
      {
        type: 'harmonyTitle',
        text: {
          type: 'translatableText',
          translationKey: translations.title,
        },
        fontSize: 'primary',
      },
      ...(layout === 'new' ? newContactFields : defaultContactFields),
    ],
    subFlow: SubFlow.Common,
    nextStep,
  } as const satisfies AnyStepConfiguration
}

export default personal_details
